import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Coffee, Share, MessageSquare, BookOpen, Code, LucideIcon, ExternalLink } from 'lucide-react';
import { GradientTitle } from '../components/ui';
import { OutlineButton } from '../components/ui/buttons/OutlineButton';
import { useNavigate } from 'react-router-dom';

// Component that embeds the Buy Me a Coffee widget directly in the page using iframe
const BuyMeCoffeeWidget: React.FC = () => {
  // Use the direct widget page URL
  const iframeSrc = "https://www.buymeacoffee.com/widget/page/cognitivebiaslab?description=Support%20me%20on%20Buy%20me%20a%20coffee!&color=%23FF5F5F";
  
  return (
    <div className="w-full flex justify-center my-6">
      <div className="bg-white rounded-xl shadow-sm p-4 w-full max-w-xl">
        <iframe 
          className="bmc-iframe" 
          src={iframeSrc} 
          title="Buy me a coffee"
          style={{ 
            width: '100%',
            height: '600px',
            border: 'none',
            overflow: 'hidden'
          }}
          frameBorder="0"
          scrolling="no"
          allow="payment"
        />
      </div>
    </div>
  );
};

// Support reason card component
interface SupportReasonProps {
  title: string;
  description: string;
  icon: LucideIcon;
  button?: React.ReactNode;
}

const SupportReason: React.FC<SupportReasonProps> = ({ title, description, icon: Icon, button }) => {
  return (
    <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-6">
      <div className="flex items-center mb-4">
        <div className="bg-gradient-to-br from-orange-100 to-pink-100 rounded-xl p-3 w-12 h-12 flex items-center justify-center mr-4">
          <Icon className="w-6 h-6 text-orange-500" />
        </div>
        <h3 className="text-xl font-semibold text-gray-800">{title}</h3>
      </div>
      <p className="text-gray-600 mb-4">{description}</p>
      {button && (
        <div className="mt-2">
          {button}
        </div>
      )}
    </div>
  );
};

export const SupportPage: React.FC = () => {
  const navigate = useNavigate();
  const [copyMessage, setCopyMessage] = useState<string>('');
  
  // Buy Me a Coffee widget URL
  const iframeSrc = "https://www.buymeacoffee.com/widget/page/cognitivebiaslab?description=Support%20me%20on%20Buy%20me%20a%20coffee!&color=%23FF5F5F";

  // Function to copy website URL to clipboard
  const handleShare = () => {
    const websiteUrl = "https://www.cognitivebiaslab.com";
    navigator.clipboard.writeText(websiteUrl)
      .then(() => {
        setCopyMessage('URL copied to clipboard!');
        setTimeout(() => setCopyMessage(''), 3000);
      })
      .catch(err => {
        console.error('Failed to copy URL: ', err);
      });
  };

  // Function to navigate to contact page
  const handleFeedback = () => {
    navigate('/contact');
  };

  const supportReasons = [
    {
      title: "Free Educational Resources",
      description: "We believe knowledge should be accessible to everyone. Your support helps us keep our content free while maintaining high quality.",
      icon: BookOpen
    },
    {
      title: "Ongoing Development",
      description: "We're constantly improving our tools and adding new content. Your support directly fuels these improvements and innovations.",
      icon: Code
    },
    {
      title: "Community Growth",
      description: "Help us build a community of critical thinkers who understand and overcome cognitive biases in their daily lives.",
      icon: Share
    }
  ];

  const otherWays = [
    {
      title: "Share Our Resources",
      description: "Know someone who might benefit from our tools? Sharing is caring! Help others discover how to improve their thinking.",
      icon: Share,
      button: (
        <div>
          <OutlineButton onClick={handleShare} className="flex items-center gap-2">
            <Share className="w-4 h-4" /> Share Website
          </OutlineButton>
          {copyMessage && (
            <div className="text-green-600 text-sm mt-2 font-medium">
              {copyMessage}
            </div>
          )}
        </div>
      )
    },
    {
      title: "Provide Feedback",
      description: "Your insights help us improve. We'd love to hear your suggestions, ideas, or even just what's working well for you.",
      icon: MessageSquare,
      button: (
        <OutlineButton onClick={handleFeedback} className="flex items-center gap-2">
          <ExternalLink className="w-4 h-4" /> Contact Us
        </OutlineButton>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-orange-50 py-12">
      <Helmet prioritizeSeoTags>
        <title>Support Us | Buy Me a Coffee</title>
        <meta 
          name="description" 
          content="Support the Cognitive Bias Lab project by buying us a coffee. Your contributions help us continue to provide high-quality resources about cognitive biases."
          key="description"
        />
        <meta 
          property="og:title" 
          content="Support Cognitive Bias Lab" 
          key="og:title" 
        />
        <meta 
          property="og:description" 
          content="Support the Cognitive Bias Lab project by buying us a coffee. Your contributions help us continue to provide high-quality resources about cognitive biases."
          key="og:description"
        />
        <link rel="canonical" href="https://www.cognitivebiaslab.com/support/" key="canonical" data-source="page-component" />
      </Helmet>

      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <GradientTitle className="text-3xl md:text-4xl mb-4" useH1={true}>
            Support Us
          </GradientTitle>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Your support helps us grow this resource. Together, we can help more people understand how our minds work and make better decisions.
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          {/* Desktop: Two-column layout for Buy Me a Coffee and Other Ways to Support */}
          <div className="flex flex-col lg:flex-row gap-8 mb-12">
            {/* Buy Me a Coffee column */}
            <div className="lg:w-1/2 bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-semibold text-gray-800 text-center flex items-center justify-center gap-2 mb-6">
                <div className="bg-gradient-to-br from-orange-100 to-pink-100 rounded-xl p-3 w-12 h-12 flex items-center justify-center">
                  <Coffee className="w-6 h-6 text-orange-500" />
                </div>
                Buy Me a Coffee
              </h2>
              
              <iframe 
                className="bmc-iframe mx-auto" 
                src={iframeSrc} 
                title="Buy me a coffee"
                style={{ 
                  width: '100%',
                  height: '600px',
                  border: 'none',
                  overflow: 'hidden',
                  maxWidth: '500px',
                  display: 'block'
                }}
                frameBorder="0"
                scrolling="no"
                allow="payment"
              />
              
              <p className="text-center text-gray-600 max-w-md mx-auto mt-4">
                Every coffee counts! Your generosity fuels our passion to create better tools and resources for understanding cognitive biases.
              </p>
            </div>
            
            {/* Other Ways to Support column */}
            <div className="lg:w-1/2">
              <h2 className="text-2xl font-semibold mb-6 text-gray-800 text-center">Other Ways to Support</h2>
              <div className="flex flex-col gap-6">
                {otherWays.map((way) => (
                  <SupportReason 
                    key={way.title}
                    title={way.title}
                    description={way.description}
                    icon={way.icon}
                    button={way.button}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Why Support Us section - full width */}
          <section>
            <h2 className="text-2xl font-semibold mb-6 text-gray-800 text-center">Why Support Us?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {supportReasons.map((reason) => (
                <SupportReason 
                  key={reason.title}
                  title={reason.title}
                  description={reason.description}
                  icon={reason.icon}
                />
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default SupportPage; 